'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  AlertCircle,
  Check,
  ExternalLink,
  Loader2,
  Printer,
  RefreshCw,
  ScanLine,
  Usb
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

import type { DiscoveredDevice, DeviceType } from '../types';
import { useScannerDiscovery } from './use-scanner-discovery';
import { usePrinterDiscovery } from './use-printer-discovery';
import { useBarcodeDiscovery } from './use-barcode-discovery';

interface DeviceDiscoveryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPick: (device: DiscoveredDevice) => void;
}

export function DeviceDiscoveryDialog({
  open,
  onOpenChange,
  onPick
}: DeviceDiscoveryDialogProps) {
  const t = useTranslations('devices');
  const [tab, setTab] = useState<DeviceType>('scanner');

  const handlePick = (device: DiscoveredDevice) => {
    onPick(device);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[640px]'>
        <DialogHeader>
          <DialogTitle>{t('discoverDialogTitle')}</DialogTitle>
          <DialogDescription>{t('discoverDialogSubtitle')}</DialogDescription>
        </DialogHeader>

        <Tabs
          value={tab}
          onValueChange={(value) => setTab(value as DeviceType)}
          className='w-full'
        >
          <TabsList className='grid w-full grid-cols-3'>
            <TabsTrigger value='scanner' className='gap-2'>
              <ScanLine className='h-4 w-4' />
              {t('tabScanner')}
            </TabsTrigger>
            <TabsTrigger value='printer' className='gap-2'>
              <Printer className='h-4 w-4' />
              {t('tabPrinter')}
            </TabsTrigger>
            <TabsTrigger value='barcode_scanner' className='gap-2'>
              <Usb className='h-4 w-4' />
              {t('tabBarcode')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value='scanner' className='mt-4'>
            <ScannerTab
              enabled={open && tab === 'scanner'}
              onPick={handlePick}
            />
          </TabsContent>
          <TabsContent value='printer' className='mt-4'>
            <PrinterTab
              enabled={open && tab === 'printer'}
              onPick={handlePick}
            />
          </TabsContent>
          <TabsContent value='barcode_scanner' className='mt-4'>
            <BarcodeTab
              enabled={open && tab === 'barcode_scanner'}
              onPick={handlePick}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

interface TabProps {
  enabled: boolean;
  onPick: (device: DiscoveredDevice) => void;
}

function ScannerTab({ enabled, onPick }: TabProps) {
  const t = useTranslations('devices');
  const { status, devices, error, reload } = useScannerDiscovery(enabled);

  if (status === 'loading') return <LoadingState label={t('discovering')} />;

  if (status === 'service-missing') {
    return (
      <ErrorState
        title={t('dwtServiceMissingTitle')}
        description={t('dwtServiceMissingDescription')}
        detail={error ?? undefined}
        onRetry={reload}
        retryLabel={t('retry')}
      />
    );
  }

  if (status === 'error') {
    return (
      <ErrorState
        title={t('discoveryError', { message: error ?? '' })}
        description=''
        onRetry={reload}
        retryLabel={t('retry')}
      />
    );
  }

  if (devices.length === 0) {
    return (
      <EmptyState
        label={t('noDevicesFound')}
        onRetry={reload}
        retryLabel={t('retry')}
      />
    );
  }

  return (
    <DeviceList
      devices={devices}
      onPick={onPick}
      icon={<ScanLine className='h-4 w-4 text-violet-600' />}
      useLabel={t('useThisDevice')}
      noSerialHint={t('noScannerSerial')}
    />
  );
}

function PrinterTab({ enabled, onPick }: TabProps) {
  const t = useTranslations('devices');
  const { status, devices, error, reload } = usePrinterDiscovery(enabled, {
    pollMs: 3000
  });

  if (status === 'loading') return <LoadingState label={t('discovering')} />;

  if (status === 'service-missing') {
    return (
      <ErrorState
        title={t('browserPrintMissingTitle')}
        description={t('browserPrintMissingDescription')}
        detail={error ?? undefined}
        onRetry={reload}
        retryLabel={t('retry')}
        secondary={
          <a
            href='https://www.zebra.com/us/en/support-downloads/software/printer-software/browser-print.html'
            target='_blank'
            rel='noopener noreferrer'
            className='text-primary inline-flex items-center gap-1 text-sm hover:underline'
          >
            <ExternalLink className='h-3.5 w-3.5' />
            {t('browserPrintLearnMore')}
          </a>
        }
      />
    );
  }

  if (status === 'error') {
    return (
      <ErrorState
        title={t('discoveryError', { message: error ?? '' })}
        description=''
        onRetry={reload}
        retryLabel={t('retry')}
      />
    );
  }

  if (devices.length === 0) {
    return (
      <EmptyState
        label={t('noDevicesFound')}
        onRetry={reload}
        retryLabel={t('retry')}
      />
    );
  }

  return (
    <DeviceList
      devices={devices}
      onPick={onPick}
      icon={<Printer className='h-4 w-4 text-blue-600' />}
      useLabel={t('useThisDevice')}
    />
  );
}

function BarcodeTab({ enabled, onPick }: TabProps) {
  const t = useTranslations('devices');
  const { status, devices, error, pickDevice } = useBarcodeDiscovery(enabled);

  if (status === 'unsupported') {
    return <ErrorState title={t('browserNotSupported')} description='' />;
  }

  return (
    <div className='space-y-4'>
      <div className='rounded-md border bg-slate-50 p-4 text-sm dark:bg-slate-900/40'>
        <p className='font-medium'>{t('hidPickDeviceTitle')}</p>
        <p className='text-muted-foreground mt-1 text-xs'>
          {t('hidPickDeviceDescription')}
        </p>
        <Button
          size='sm'
          variant='outline'
          className='mt-3 gap-2'
          onClick={() => void pickDevice()}
          disabled={status === 'loading'}
        >
          {status === 'loading' ? (
            <Loader2 className='h-3.5 w-3.5 animate-spin' />
          ) : (
            <Usb className='h-3.5 w-3.5' />
          )}
          {t('hidPickDeviceCta')}
        </Button>
      </div>

      {status === 'permission-denied' && (
        <p className='text-xs text-amber-600'>{t('hidPermissionDenied')}</p>
      )}
      {status === 'error' && (
        <p className='text-xs text-rose-600'>
          {t('discoveryError', { message: error ?? '' })}
        </p>
      )}

      {devices.length > 0 && (
        <DeviceList
          devices={devices}
          onPick={onPick}
          icon={<Usb className='h-4 w-4 text-emerald-600' />}
          useLabel={t('useThisDevice')}
        />
      )}
    </div>
  );
}

function DeviceList({
  devices,
  onPick,
  icon,
  useLabel,
  noSerialHint
}: {
  devices: DiscoveredDevice[];
  onPick: (device: DiscoveredDevice) => void;
  icon: React.ReactNode;
  useLabel: string;
  noSerialHint?: string;
}) {
  return (
    <div className='space-y-2'>
      {devices.map((device, idx) => (
        <div
          key={`${device.name}-${device.serial_number ?? idx}`}
          className='hover:border-primary/50 flex items-start justify-between gap-3 rounded-md border p-3 transition-colors'
        >
          <div className='flex items-start gap-3'>
            <span className='mt-0.5 shrink-0'>{icon}</span>
            <div className='min-w-0'>
              <p className='truncate text-sm font-medium'>{device.name}</p>
              <div className='text-muted-foreground mt-0.5 space-y-0.5 text-xs'>
                {device.serial_number ? (
                  <p className='font-mono'>{device.serial_number}</p>
                ) : noSerialHint ? (
                  <p className='italic'>{noSerialHint}</p>
                ) : null}
                {device.ip_address && (
                  <p className='font-mono'>{device.ip_address}</p>
                )}
                {device.software_version && <p>v{device.software_version}</p>}
              </div>
            </div>
          </div>
          <Button
            size='sm'
            variant='outline'
            className='shrink-0 gap-1.5'
            onClick={() => onPick(device)}
          >
            <Check className='h-3.5 w-3.5' />
            {useLabel}
          </Button>
        </div>
      ))}
    </div>
  );
}

function LoadingState({ label }: { label: string }) {
  return (
    <div className='text-muted-foreground flex items-center justify-center gap-2 py-10 text-sm'>
      <Loader2 className='h-4 w-4 animate-spin' />
      {label}
    </div>
  );
}

function EmptyState({
  label,
  onRetry,
  retryLabel
}: {
  label: string;
  onRetry?: () => void;
  retryLabel?: string;
}) {
  return (
    <div className='flex flex-col items-center justify-center gap-3 py-10 text-center'>
      <p className='text-muted-foreground text-sm'>{label}</p>
      {onRetry && retryLabel && (
        <Button size='sm' variant='outline' className='gap-2' onClick={onRetry}>
          <RefreshCw className='h-3.5 w-3.5' />
          {retryLabel}
        </Button>
      )}
    </div>
  );
}

function ErrorState({
  title,
  description,
  detail,
  onRetry,
  retryLabel,
  secondary
}: {
  title: string;
  description: string;
  detail?: string;
  onRetry?: () => void;
  retryLabel?: string;
  secondary?: React.ReactNode;
}) {
  return (
    <div className='space-y-3 rounded-md border border-rose-200 bg-rose-50 p-4 text-sm dark:border-rose-900/40 dark:bg-rose-950/30'>
      <div className='flex items-start gap-2'>
        <AlertCircle className='mt-0.5 h-4 w-4 shrink-0 text-rose-600' />
        <div className='space-y-1'>
          <p className='font-semibold text-rose-700 dark:text-rose-200'>
            {title}
          </p>
          {description && (
            <p className='text-xs text-rose-700/80 dark:text-rose-200/80'>
              {description}
            </p>
          )}
          {detail && (
            <p className='font-mono text-[10px] text-rose-600/70'>{detail}</p>
          )}
        </div>
      </div>
      <div className='flex items-center gap-3'>
        {onRetry && retryLabel && (
          <Button
            size='sm'
            variant='outline'
            className='gap-2'
            onClick={onRetry}
          >
            <RefreshCw className='h-3.5 w-3.5' />
            {retryLabel}
          </Button>
        )}
        {secondary}
      </div>
    </div>
  );
}
